/**
 * API service for widget theme operations
 * Handles fetching and updating widget theme configurations
 */

import { API_BASE } from "../utils/domain";
import { ColorValue } from "../types/color";
import { DEFAULT_THEME_COLORS } from "../dev/ThemeManager/ColorSection";
import {
  getV2EditTarget,
  v2GenerateAssetUploadUrl,
  v2GetDraft,
  v2PatchDraft,
} from "./widgetThemeApiV2";

export class WidgetThemeFetchError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "WidgetThemeFetchError";
    this.status = status;
  }
}

const BRAND_APP_V2_WIDGET_THEME_MAX_FAILURES = 3;
let brandAppV2WidgetThemeFailures = 0;
let brandAppV2WidgetThemeStopped = false;

const shouldStopBrandAppV2WidgetThemeRequests = () =>
  brandAppV2WidgetThemeStopped ||
  brandAppV2WidgetThemeFailures >= BRAND_APP_V2_WIDGET_THEME_MAX_FAILURES;

const markBrandAppV2WidgetThemeSuccess = () => {
  brandAppV2WidgetThemeFailures = 0;
  brandAppV2WidgetThemeStopped = false;
};

export const isBrandAppV2WidgetThemeFallbackStatus = (status?: number) =>
  status === 400 || status === 403 || status === 404;

const markBrandAppV2WidgetThemeFailure = (status?: number) => {
  if (isBrandAppV2WidgetThemeFallbackStatus(status)) {
    brandAppV2WidgetThemeStopped = true;
    return;
  }

  brandAppV2WidgetThemeFailures += 1;
  if (brandAppV2WidgetThemeFailures >= BRAND_APP_V2_WIDGET_THEME_MAX_FAILURES) {
    brandAppV2WidgetThemeStopped = true;
  }
};

/**
 * A successful `GET /brandapp-v2/bootstrap` is the sole signal that the
 * current host is a BrandApp V2 app (the endpoint resolves identity by
 * hostname and fails for non-V2 apps), so it drives whether this package
 * reads V2 theme endpoints vs the legacy V1 ones.
 *
 * Inlined (rather than reusing `common-ui`'s `getBootstrapShared`) because
 * `bc-widgets` is a standalone workspace package with no `common-ui`
 * dependency. The settled result is memoized for the page session so the
 * probe fires at most once; network errors resolve to `false` (→ V1).
 */
let _isBrandAppV2Probe: Promise<boolean> | null = null;
const isBrandAppV2 = (): Promise<boolean> => {
  if (!_isBrandAppV2Probe) {
    _isBrandAppV2Probe = (async () => {
      try {
        const res = await fetch(`${API_BASE}/brandapp-v2/bootstrap`);
        return res.ok;
      } catch {
        return false;
      }
    })();
  }
  return _isBrandAppV2Probe;
};

/**
 * Migrate legacy hex string color values to ColorValue objects
 * Any string starting with '#' is considered a legacy hex color
 */
const migrateColorValues = (
  widgetConfig: Record<string, any>
): { migrated: Record<string, any>; needsMigration: boolean } => {
  const migrated: Record<string, any> = {};
  let needsMigration = false;

  for (const [key, value] of Object.entries(widgetConfig)) {
    if (typeof value === "string" && value.startsWith("#")) {
      // Legacy hex string - convert to ColorValue
      migrated[key] = { type: "custom", value } as ColorValue;
      needsMigration = true;
    } else {
      migrated[key] = value;
    }
  }

  return { migrated, needsMigration };
};

/**
 * Ensure all DEFAULT_THEME_COLORS exist in theme colors
 * Returns missing colors that need to be added
 */
const getMissingThemeColors = (
  themeColors: Record<string, string>
): Record<string, string> => {
  const missing: Record<string, string> = {};
  for (const [colorKey, colorValue] of Object.entries(DEFAULT_THEME_COLORS)) {
    if (!(colorKey in themeColors)) {
      missing[colorKey] = colorValue as string;
    }
  }
  return missing;
};

/**
 * Sidebar widget sizing keys that were saved with old defaults and should be
 * removed so the updated code defaults (matching Figma) take effect.
 * Map of key → set of stale values to strip. Use `"*"` in the set to always clear the key.
 */
const CHAT_INPUT_STALE: Record<string, Set<unknown>> = {
  shadow: new Set(["8px 8px 14px 0px rgba(0, 0, 0, 0.03)", "8px 8px 14px 0 rgba(0, 0, 0, 0.03)", "8px 8px 6px 0px rgba(0, 0, 0, 0.02)"]),
  borderColor: new Set(["*"]),
};

const WIDGET_STALE_KEYS: Record<string, Record<string, Set<unknown>>> = {
  "concierge-sidebar": {
    logoIconSize: new Set([20, 22, 32]),
    avatarSize: new Set([20, 22]),
    composeIconSize: new Set([18]),
    sidebarIconSize: new Set([20, 28]),
    avatarFontSize: new Set([8]),
    chatHistoryTitleFontSize: new Set([14, 16]),
    labelFontSize: new Set([14, 16]),
    collapsedWidth: new Set([40]),
  },
  "concierge-home-greeting": {
    greetingFontSize: new Set([28]),
    greetingFontFamily: new Set(["*"]),
    greetingMarginBottom: new Set([8, 24]),
  },
  "concierge-home-subtitle": {
    fontSize: new Set([32]),
    fontFamily: new Set(["*"]),
  },
  "product-card-carousel": {
    titleLineClamp: new Set([2]),
    // Older defaults were 18 / 16 / 20; mock + code default are now 14.
    titleFontSize: new Set([18, 16, 20]),
    titleLineHeight: new Set([1.333]),
    showRetailers: new Set([false]),
    // Old carousel default was 24/20; mock + code default are now 16.
    priceFontSize: new Set([24]),
    priceFontSizeMobile: new Set([20, 24]),
  },
  "concierge-chat-input": CHAT_INPUT_STALE,
  "concierge-panel-chat-input": CHAT_INPUT_STALE,
  "concierge-conversation-chat-input": CHAT_INPUT_STALE,
  "concierge-panel-conversation-chat-input": CHAT_INPUT_STALE,
  "chat-input": CHAT_INPUT_STALE,
};

const migrateStaleDefaults = (
  widgetId: string,
  config: Record<string, any>
): { migrated: Record<string, any>; needsMigration: boolean } => {
  const staleKeys = WIDGET_STALE_KEYS[widgetId];
  if (!staleKeys) return { migrated: config, needsMigration: false };

  const migrated = { ...config };
  let needsMigration = false;

  for (const [key, staleValues] of Object.entries(staleKeys)) {
    if (key in migrated && (staleValues.has("*") || staleValues.has(migrated[key]))) {
      delete migrated[key];
      needsMigration = true;
    }
  }

  return { migrated, needsMigration };
};

/** Theme font key the backend assigns to a Concierge-Studio-uploaded font. */
const STUDIO_FONT_KEY = "studio-brand-font";

/**
 * Point every per-widget font choice at the Studio-uploaded font.
 *
 * Concierge Studio is the top theme tier (brand < app < studio), so changing the
 * font in Studio has to change every widget. Widgets resolve their own font as
 * `themeFonts[state.<...>FontFamily]`, and a brand theme can pin those to its own
 * font keys (PetArmor pins nine widgets to `librefranklin-*`), which used to win
 * over the Studio upload. Repoint them.
 *
 * A value counts as a font reference when it names an existing key in
 * `config.fonts` — read from the theme's own data rather than guessed from field
 * names, so it covers `fontFamily`, `titleFontFamily`, `greetingFontFamily`, and
 * any future variant without a list to maintain.
 *
 * The rewritten fields are also marked studio-controlled, which is what makes the
 * theme editor lock them: without that, saving an unrelated field on one of these
 * widgets would persist the rewrite into the brand's stored theme row.
 *
 * No-op when Studio has no font uploaded — the brand's own choices stand.
 */
const applyStudioFontToWidgetConfigs = (theme: any): void => {
  const fonts = theme.config?.fonts;
  const widgetConfig = theme.config?.widgetConfig;
  if (!fonts?.[STUDIO_FONT_KEY] || !widgetConfig) return;

  const controlledWidgets: Record<string, string[]> = {
    ...(theme.studioControlledKeys?.widgetConfig ?? {}),
  };
  const nextWidgetConfig: Record<string, any> = {};
  let rewroteAnything = false;

  for (const [widgetId, config] of Object.entries(widgetConfig)) {
    if (!config || typeof config !== "object") {
      nextWidgetConfig[widgetId] = config;
      continue;
    }

    const updated: Record<string, unknown> = { ...(config as Record<string, unknown>) };
    const locked = new Set<string>(controlledWidgets[widgetId] ?? []);
    let changed = false;

    for (const [key, value] of Object.entries(updated)) {
      if (typeof value !== "string" || value === STUDIO_FONT_KEY) continue;
      if (!fonts[value]) continue;
      updated[key] = STUDIO_FONT_KEY;
      locked.add(key);
      changed = true;
    }

    nextWidgetConfig[widgetId] = changed ? updated : config;
    if (changed) {
      controlledWidgets[widgetId] = [...locked];
      rewroteAnything = true;
    }
  }

  if (!rewroteAnything) return;

  theme.config = { ...theme.config, widgetConfig: nextWidgetConfig };
  theme.studioControlledKeys = {
    ...(theme.studioControlledKeys ?? {}),
    widgetConfig: controlledWidgets,
  };
};

/**
 * Process theme data to migrate legacy color values and add missing default colors
 * Returns the processed theme and whether any updates were needed
 */
const processThemeData = (
  theme: any
): { processedTheme: any; needsUpdate: boolean } => {
  if (!theme) return { processedTheme: theme, needsUpdate: false };

  let needsUpdate = false;
  const processedTheme = { ...theme };

  // Ensure config exists
  if (!processedTheme.config) {
    processedTheme.config = {};
  }

  // Check and add missing default theme colors
  const themeColors = processedTheme.config.colors || {};
  const missingColors = getMissingThemeColors(themeColors);
  if (Object.keys(missingColors).length > 0) {
    processedTheme.config.colors = { ...themeColors, ...missingColors };
    needsUpdate = true;
  }

  // Check and migrate widget configs
  if (processedTheme.config.widgetConfig) {
    const migratedWidgetConfig: Record<string, any> = {};

    for (const [widgetId, config] of Object.entries(
      processedTheme.config.widgetConfig
    )) {
      let current = config as Record<string, any>;

      const { migrated: colorMigrated, needsMigration: colorNeedsMigration } =
        migrateColorValues(current);
      current = colorMigrated;
      if (colorNeedsMigration) needsUpdate = true;

      if (widgetId in WIDGET_STALE_KEYS) {
        const { migrated: staleMigrated, needsMigration: staleNeedsMigration } =
          migrateStaleDefaults(widgetId, current);
        current = staleMigrated;
        if (staleNeedsMigration) needsUpdate = true;
      }

      migratedWidgetConfig[widgetId] = current;
    }

    processedTheme.config.widgetConfig = migratedWidgetConfig;
  }

  // Studio font wins over per-widget font pins. Deliberately does not set
  // `needsUpdate`: this is an override applied on read, not a migration of the
  // brand's stored theme.
  applyStudioFontToWidgetConfigs(processedTheme);

  return { processedTheme, needsUpdate };
};

/**
 * Update the theme config in the background after migration
 * This is a fire-and-forget operation
 */
const updateMigratedThemeInBackground = async (
  themeId: string,
  config: any
): Promise<void> => {
  try {
    // We need an editThemeToken to update, but we may not have one
    // For now, log that migration is needed - the theme will be migrated
    // when it's next saved through the normal editing flow
    console.log(
      `Theme ${themeId} needs migration. Will be migrated on next save.`
    );
    // In the future, we could add a dedicated migration endpoint that doesn't require auth
  } catch (error) {
    console.error(`Failed to migrate theme ${themeId}:`, error);
  }
};

const fetchLegacyPublishedWidgetTheme = async (appId: string): Promise<any> => {
  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/published-by-app-id?appId=${appId}`
  );

  if (!response.ok) {
    throw new WidgetThemeFetchError(
      "Failed to fetch published widget theme",
      response.status
    );
  }

  const theme = await response.json();

  // Process theme data: migrate legacy colors and add missing default colors
  const { processedTheme, needsUpdate } = processThemeData(theme);

  // If migration was needed and we have a theme ID, attempt to update in the background
  if (needsUpdate && processedTheme?.appThemeId) {
    updateMigratedThemeInBackground(
      processedTheme.appThemeId,
      processedTheme.config
    );
  }

  return processedTheme;
};

/**
 * Fetch a widget theme by theme ID
 * Public endpoint, no authentication required
 * Automatically migrates legacy color values and adds missing default colors
 *
 * @param themeId - The theme ID to fetch
 * @returns Promise<any> - The theme data including widget configurations
 * @throws Error if the fetch fails
 */
export const fetchWidgetTheme = async (themeId: string): Promise<any> => {
  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/by-theme-id?appThemeId=${themeId}`
  );

  if (!response.ok) {
    throw new WidgetThemeFetchError("Failed to fetch widget theme", response.status);
  }

  const theme = await response.json();

  // Process theme data: migrate legacy colors and add missing default colors
  const { processedTheme, needsUpdate } = processThemeData(theme);

  // If migration was needed, attempt to update in the background
  if (needsUpdate) {
    updateMigratedThemeInBackground(themeId, processedTheme.config);
  }

  return processedTheme;
};

/**
 * Fetch the published widget theme for an app by appId
 * Public endpoint, no authentication required
 * Automatically migrates legacy color values and adds missing default colors
 *
 * @param appId - The app ID (typically the domain name)
 * @returns Promise<any> - The published theme data including widget configurations, or null if no published theme
 * @throws Error if the fetch fails
 */
export const fetchPublishedWidgetTheme = async (
  appId: string,
  options?: { knownBrandAppV2?: boolean }
): Promise<any> => {
  // Editor-mode shortcut: when the page carries a V2 edit target
  // (`?appId=&scope=&version=&editThemeToken=` from portal "Edit live"), prefer
  // the saved draft over the published version so the ThemeManager sidebar
  // shows the row being edited. Independent of the V2 probe below — an edit
  // target is itself proof of a V2 authoring session. Anonymous storefront
  // visitors never have these URL params.
  const editTarget = getV2EditTarget();
  if (editTarget) {
    try {
      // Include studio overlay so Edit live matches V1: Studio-owned
      // tokens render, and `studioControlledKeys` lock those fields.
      // Patches still read raw via `v2PatchVersion` (no overlay).
      const {
        data,
        studioControlledKeys,
        studioAvailability,
        studioAgentName,
        studioAgreement,
        studioSupportSkills,
        studioSupportBranding,
        studioVoiceSkills,
        studioVoiceBranding,
      } = await v2GetDraft(editTarget, { includeStudioOverlay: true });
      if (data && Object.keys(data).length > 0) {
        const wrapped = {
          config: data,
          studioControlledKeys,
          studioAvailability,
          studioAgentName,
          studioAgreement,
          studioSupportSkills,
          studioSupportBranding,
          studioVoiceSkills,
          studioVoiceBranding,
        };
        const { processedTheme } = processThemeData(wrapped);
        return processedTheme;
      }
      // Empty draft → fall through to the published fetch below.
    } catch {
      // Token expired / invalid / network blip — silently fall through to
      // published; the next granular write will surface the auth error.
    }
  }

  // Hosts that already loaded `/bootstrap` (and seeded `appBootstrapV2`) should
  // not probe that endpoint again just to decide V2 vs V1 theme.
  const isV2App = options?.knownBrandAppV2 || (await isBrandAppV2());
  if (isV2App) {
    // V2: identity resolves server-side from the `x-bc-app-id` header injected
    // by the MTD edge, so the legacy `appId` argument is ignored. Once
    // bootstrap succeeds, do not fall back to V1 theme data for actual V2
    // theme data/runtime failures; using V1 can mask drift or create the wrong
    // legacy rows for pure-V2 apps. A 400/403/404 from the V2 theme endpoint
    // means this ingress cannot serve the V2 theme path, so legacy fallback is
    // still appropriate for hybrid/STD hosts.
    //
    // V2 themes are scope-versioned — there's no `appThemeId`, and the
    // background migration fast-path is skipped because V2 themes are already
    // in the canonical shape (stored pre-merged in `prod-bc-brand-app-theme-v2`).
    // We reshape `{scope, version, data}` into the `{config: …}` envelope
    // `processThemeData` still expects.
    if (shouldStopBrandAppV2WidgetThemeRequests()) {
      throw new WidgetThemeFetchError(
        "BrandApp V2 widget theme requests stopped after repeated failures",
        503
      );
    }

    const response = await fetch(
      `${API_BASE}/brandapp-v2/get-theme-by-scope?scope=widget`
    );

    // 204 = this app has published no `widget` theme scope yet. It is a normal
    // state for a store whose concierge hydration has not finished, NOT an error
    // — but `response.ok` is true for 204, so this used to fall through to
    // `response.json()` on an EMPTY body, throw a raw SyntaxError, get retried
    // three times, and end with no theme at all.
    //
    // With no theme every widget colour resolves through `resolveColor`'s
    // `#000000` default, so the concierge rendered as a solid black panel with
    // black pills and black-on-black text. Observed live on brandclub-dev,
    // 2026-08-18.
    //
    // Return the defaults instead: an unthemed store gets a readable widget
    // rather than a black box, and the retry storm stops.
    if (response.status === 204) {
      return { config: { colors: { ...DEFAULT_THEME_COLORS } } };
    }

    if (!response.ok) {
      if (isBrandAppV2WidgetThemeFallbackStatus(response.status)) {
        return fetchLegacyPublishedWidgetTheme(appId);
      }

      markBrandAppV2WidgetThemeFailure(response.status);
      throw new WidgetThemeFetchError(
        "Failed to fetch BrandApp V2 widget theme",
        response.status
      );
    }

    const payload = (await response.json()) as {
      scope?: string;
      version?: string | null;
      data?: Record<string, any>;
      // studio* overlay fields (studioAgentName, studioAvailability, …)
      // attached by the backend for scope=widget — forwarded below so the
      // useStudio* hooks read the same top-level shape as on the V1 path.
      [key: string]: any;
    } | null;

    if (!payload?.data) {
      markBrandAppV2WidgetThemeFailure();
      throw new WidgetThemeFetchError(
        "BrandApp V2 widget theme response did not include data",
        502
      );
    }

    const { scope: _scope, version: _version, data, ...studioFields } = payload;
    const wrapped = { ...studioFields, config: data };
    const { processedTheme } = processThemeData(wrapped);
    markBrandAppV2WidgetThemeSuccess();
    return processedTheme;
  }

  return fetchLegacyPublishedWidgetTheme(appId);
};

/**
 * Validate an edit theme token without performing any mutation.
 * Returns true if the token is valid for the given theme, false otherwise.
 */
export const validateEditThemeToken = async ({
  themeId,
  editThemeToken,
}: {
  themeId: string;
  editThemeToken: string;
}): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE}/brand-app-widget-theme/validate-edit-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appThemeId: themeId,
          editThemeToken,
        }),
      }
    );

    if (!response.ok) return false;
    const data = await response.json();
    return data.valid === true;
  } catch {
    return false;
  }
};

/**
 * Update widget configuration within a theme
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, widgetId, widgetState, and editThemeToken
 * @returns Promise<any> - The updated theme data
 * @throws Error if the update fails or token is invalid
 */
export const updateWidgetConfig = async ({
  themeId,
  widgetId,
  widgetState,
  editThemeToken,
}: {
  themeId: string;
  widgetId: string;
  widgetState: any;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    // V1 backend deep-merged `widgetState` into `config.widgetConfig[widgetId]`;
    // mirror that to stay compatible with partial updates the ThemeManager
    // sends (e.g. a single color on one widget without resending the full state).
    const patched = await v2PatchDraft(v2, (data) => {
      const current = data.widgetConfig[widgetId] ?? {};
      data.widgetConfig[widgetId] = { ...current, ...widgetState };
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/update-widget-config`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        widgetId,
        widgetState,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to update widget config";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Set a theme color (create or update)
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, colorKey, colorValue, and editThemeToken
 * @returns Promise<any> - The updated theme data
 * @throws Error if the update fails or token is invalid
 */
export const setThemeColor = async ({
  themeId,
  colorKey,
  colorValue,
  editThemeToken,
}: {
  themeId: string;
  colorKey: string;
  colorValue: string;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    const patched = await v2PatchDraft(v2, (data) => {
      data.colors[colorKey] = colorValue;
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/set-theme-color`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        colorKey,
        colorValue,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to set theme color";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Delete a theme color
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, colorKey, and editThemeToken
 * @returns Promise<any> - The updated theme data
 * @throws Error if the delete fails or token is invalid
 */
export const deleteThemeColor = async ({
  themeId,
  colorKey,
  editThemeToken,
}: {
  themeId: string;
  colorKey: string;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    const patched = await v2PatchDraft(v2, (data) => {
      delete data.colors[colorKey];
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/delete-theme-color`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        colorKey,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to delete theme color";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Rename a theme color key atomically
 * Requires a valid editThemeToken for authentication
 * This operation atomically deletes the old color key and creates a new one with the same value.
 * The backend will handle updating any references to ensure widgets continue to work.
 *
 * @param params - Object containing themeId, oldColorKey, newColorKey, and editThemeToken
 * @returns Promise<any> - The updated theme data, optionally including affectedWidgetIds
 * @throws Error if the rename fails or token is invalid
 */
export const renameThemeColor = async ({
  themeId,
  oldColorKey,
  newColorKey,
  editThemeToken,
}: {
  themeId: string;
  oldColorKey: string;
  newColorKey: string;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    // Atomic rename from the client's POV: we do it in a single
    // GET-patch-POST. Anything referencing the old key by name in widget
    // configs is left untouched (matches V1 — the backend used to update
    // references too, but ThemeManager already doesn't depend on that
    // side-effect in normal rename flows).
    const patched = await v2PatchDraft(v2, (data) => {
      if (oldColorKey in data.colors) {
        data.colors[newColorKey] = data.colors[oldColorKey];
        delete data.colors[oldColorKey];
      }
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/rename-theme-color`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        oldColorKey,
        newColorKey,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to rename theme color";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/** Font value stored in config.fonts */
export interface ThemeFontValue {
  name: string;
  url: string;
  format: string;
  /** Per-weight files for multi-weight uploads (e.g. Concierge Studio brand fonts). */
  weights?: { weight: number; url: string }[];
}

/** Icon value stored in config.icons */
export interface ThemeIconValue {
  name: string;
  url: string;
}

/**
 * Generate a signed upload URL for theme assets (fonts, icons)
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, fileName, contentType, assetType, and editThemeToken
 * @returns Promise with signedUploadUrl and objectPublicUrl
 * @throws Error if the request fails or token is invalid
 */
export const generateAssetUploadUrl = async ({
  themeId,
  fileName,
  contentType,
  assetType,
  editThemeToken,
}: {
  themeId: string;
  fileName: string;
  contentType: string;
  assetType: "font" | "icon";
  editThemeToken: string;
}): Promise<{ signedUploadUrl: string; objectPublicUrl: string }> => {
  const v2 = getV2EditTarget();
  if (v2) {
    return v2GenerateAssetUploadUrl(v2, { fileName, contentType, assetType });
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/generate-asset-upload-url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        fileName,
        contentType,
        assetType,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to generate upload URL";
    try {
      const error = await response.json();
      errorMessage = error.errorMessage || error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  if (data.status === "failed") {
    throw new Error(data.errorMessage || "Failed to generate upload URL");
  }

  return {
    signedUploadUrl: data.signedUploadUrl,
    objectPublicUrl: data.objectPublicUrl,
  };
};

/**
 * Upload a file directly to S3 using a signed URL
 *
 * @param params - Object containing file and signedUploadUrl
 * @returns Promise<void>
 * @throws Error if the upload fails
 */
export const uploadAssetToS3 = async ({
  file,
  signedUploadUrl,
}: {
  file: File;
  signedUploadUrl: string;
}): Promise<void> => {
  const response = await fetch(signedUploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to upload file to S3");
  }
};

/**
 * Set a theme font (create or update)
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, fontKey, fontValue, and editThemeToken
 * @returns Promise<any> - The updated theme data
 * @throws Error if the update fails or token is invalid
 */
export const setThemeFont = async ({
  themeId,
  fontKey,
  fontValue,
  editThemeToken,
}: {
  themeId: string;
  fontKey: string;
  fontValue: ThemeFontValue;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    const patched = await v2PatchDraft(v2, (data) => {
      data.fonts[fontKey] = fontValue;
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/set-theme-font`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        fontKey,
        fontValue,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to set theme font";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Delete a theme font
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, fontKey, and editThemeToken
 * @returns Promise<any> - The updated theme data
 * @throws Error if the delete fails or token is invalid
 */
export const deleteThemeFont = async ({
  themeId,
  fontKey,
  editThemeToken,
}: {
  themeId: string;
  fontKey: string;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    const patched = await v2PatchDraft(v2, (data) => {
      delete data.fonts[fontKey];
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/delete-theme-font`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        fontKey,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to delete theme font";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Set a theme icon (create or update)
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, iconKey, iconValue, and editThemeToken
 * @returns Promise<any> - The updated theme data
 * @throws Error if the update fails or token is invalid
 */
export const setThemeIcon = async ({
  themeId,
  iconKey,
  iconValue,
  editThemeToken,
}: {
  themeId: string;
  iconKey: string;
  iconValue: ThemeIconValue;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    const patched = await v2PatchDraft(v2, (data) => {
      data.icons[iconKey] = iconValue;
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/set-theme-icon`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        iconKey,
        iconValue,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to set theme icon";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Delete a theme icon
 * Requires a valid editThemeToken for authentication
 *
 * @param params - Object containing themeId, iconKey, and editThemeToken
 * @returns Promise<any> - The updated theme data
 * @throws Error if the delete fails or token is invalid
 */
export const deleteThemeIcon = async ({
  themeId,
  iconKey,
  editThemeToken,
}: {
  themeId: string;
  iconKey: string;
  editThemeToken: string;
}): Promise<any> => {
  const v2 = getV2EditTarget();
  if (v2) {
    const patched = await v2PatchDraft(v2, (data) => {
      delete data.icons[iconKey];
    });
    return { config: patched };
  }

  const response = await fetch(
    `${API_BASE}/brand-app-widget-theme/delete-theme-icon`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appThemeId: themeId,
        iconKey,
        editThemeToken,
      }),
    }
  );

  if (!response.ok) {
    let errorMessage = "Failed to delete theme icon";
    try {
      const error = await response.json();
      errorMessage = error.message || errorMessage;
    } catch {
      // If error response isn't JSON, use default message
    }
    throw new Error(errorMessage);
  }

  return response.json();
};
